import { useEffect, useMemo, useRef, useState } from "react"
import { ChevronDown, Folder, FileText, Loader2, BookOpen } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@f2e/ui/components/base"
import { FetchStatus, useListKnowledgeBaseNodesApi } from "@/hooks/api"
import type { KnowledgeNodeSchema } from "@/hooks/api"
import { useStore } from "@/store"

interface KnowledgeNodeDropdownProps {
  workspaceId: string
  onFileSelect: (node: KnowledgeNodeSchema) => void
  disabled?: boolean
  className?: string
}

/**
 * KnowledgeNodeDropdown
 *
 * Replaces the legacy ContextSourceDropdown with KnowledgeNode browsing:
 * - Root menu lists KB root folders (parent_id omitted).
 * - Submenus browse folder children recursively (fetch-on-hover).
 * - Selecting a FILE node invokes `onFileSelect`.
 */
export const KnowledgeNodeDropdown = ({
  workspaceId,
  onFileSelect,
  disabled = false,
  className,
}: KnowledgeNodeDropdownProps) => {
  const { t } = useTranslation()
  const organizationId = useStore((s) => s.currentOrganization?.id)

  // NOTE: we use TWO independent hooks:
  // - one for KB roots (single request → we can rely on hook data/error)
  // - one for folder children (many requests → we rely on per-request callbacks to avoid overwriting shared hook state)
  const rootsApi = useListKnowledgeBaseNodesApi()
  const childrenApi = useListKnowledgeBaseNodesApi()

  // childrenByParentId[parentId] = list of child nodes; undefined indicates "loading" for an initiated fetch
  const [childrenByParentId, setChildrenByParentId] = useState<
    Record<string, KnowledgeNodeSchema[] | undefined>
  >({})

  // Track active requests to avoid races when the user hovers quickly
  const activeRequests = useRef<Record<string, boolean>>({})

  const foldersFirst = useMemo(
    () => (a: KnowledgeNodeSchema, b: KnowledgeNodeSchema) => {
      if (a.node_type === "FOLDER" && b.node_type !== "FOLDER") return -1
      if (a.node_type !== "FOLDER" && b.node_type === "FOLDER") return 1
      return a.name.localeCompare(b.name)
    },
    []
  )

  const ensureChildrenLoaded = (parentId: string) => {
    if (!workspaceId) return
    if (!organizationId) return
    if (childrenByParentId[parentId] !== undefined) return
    if (activeRequests.current[parentId]) return

    activeRequests.current[parentId] = true
    // Set to undefined explicitly to show loading state for this parent
    setChildrenByParentId((prev) => ({ ...prev, [parentId]: undefined }))

    childrenApi
      .fetchFn({
        urlBuilderParams: {
          organizationId,
          workspaceId,
        },
        searchParams: new URLSearchParams({
          parent_id: parentId,
        }),
        callbackFn: (resp) => {
          setChildrenByParentId((prev) => ({
            ...prev,
            [parentId]: (resp?.data ?? []).slice().sort(foldersFirst),
          }))
          delete activeRequests.current[parentId]
        },
      })
      .catch((err) => {
        console.error("Failed to list knowledge node children", err)
        setChildrenByParentId((prev) => ({ ...prev, [parentId]: [] }))
        delete activeRequests.current[parentId]
      })
  }

  useEffect(() => {
    if (!workspaceId) return
    if (!organizationId) return
    // Reset subtree cache when workspace changes
    setChildrenByParentId({})
    activeRequests.current = {}

    rootsApi.fetchFn({
      urlBuilderParams: {
        organizationId,
        workspaceId,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, organizationId])

  if (!workspaceId) return null
  if (!organizationId) return null

  // Root nodes API returns folders only by contract, but keep it defensive.
  const kbRoots = (rootsApi.data?.data ?? []).filter(
    (n) => n.node_type === "FOLDER"
  )

  if (rootsApi.status === FetchStatus.Loading) {
    return (
      <div className={className}>
        <Button
          variant="outline"
          size="sm"
          disabled
          className="justify-between"
        >
          {t("common.loading")}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </div>
    )
  }

  if (rootsApi.error) {
    console.error("Failed to list knowledge bases", rootsApi.error)
  }

  if (kbRoots.length === 0) return null

  const renderChildren = (parentId: string) => {
    const children = childrenByParentId[parentId]

    if (children === undefined) {
      return (
        <DropdownMenuItem disabled>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          {t("common.loading")}
        </DropdownMenuItem>
      )
    }

    const visibleChildren =
      children
        ?.filter((node) => {
          if (node.node_type === "FOLDER") return true
          // Hide non-ready files; only show READY files that have a snapshot (ContextObj) attached.
          return (
            node.node_type === "FILE" &&
            node.status === "READY" &&
            !!node.snapshot_object_id
          )
        })
        .slice()
        .sort(foldersFirst) ?? []

    if (visibleChildren.length === 0) {
      return (
        <DropdownMenuItem disabled>
          {t("common.contextSourceDropdown.noObjects")}
        </DropdownMenuItem>
      )
    }

    return (
      <>
        {visibleChildren.map((node) => {
          if (node.node_type === "FOLDER") {
            return (
              <DropdownMenuSub key={node.id}>
                <DropdownMenuSubTrigger
                  onFocus={() => ensureChildrenLoaded(String(node.id))}
                  onMouseEnter={() => ensureChildrenLoaded(String(node.id))}
                >
                  <div className="flex items-center gap-2">
                    <Folder className="w-4 h-4 text-muted-foreground" />
                    <span>{node.name}</span>
                  </div>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="max-h-[50vh] overflow-auto">
                    {renderChildren(String(node.id))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )
          }

          // At this point only READY files with snapshot_object_id should remain,
          // but keep the guard defensive.
          const disabledFile =
            !node.snapshot_object_id || node.status !== "READY"
          return (
            <DropdownMenuItem
              key={node.id}
              disabled={disabledFile}
              onClick={() => {
                if (disabledFile) return
                onFileSelect(node)
              }}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <span>{node.name}</span>
              </div>
            </DropdownMenuItem>
          )
        })}
      </>
    )
  }

  return (
    <div className={className}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            className="justify-between"
          >
            {t("common.attachment.selectFromCloud")}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-80 max-h-[60vh] overflow-auto">
          <DropdownMenuGroup>
            {kbRoots.map((kb) => (
              <DropdownMenuSub key={kb.id}>
                <DropdownMenuSubTrigger
                  onFocus={() => ensureChildrenLoaded(String(kb.id))}
                  onMouseEnter={() => ensureChildrenLoaded(String(kb.id))}
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-muted-foreground" />
                    <span>{kb.name}</span>
                  </div>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="max-h-[50vh] overflow-auto">
                    {renderChildren(String(kb.id))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            ))}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default KnowledgeNodeDropdown
